import { IsAnyOrUnknown } from "./type-utils";

export function isObject(data: unknown): data is object {
  return data !== null && typeof data === "object" && !Array.isArray(data);
}

export function parseObject(data: unknown, defaultValue = {}): object {
  if (!isObject(data)) {
    return defaultValue;
  }

  return data;
}

export function get<
  T extends string | number | symbol,
  ReturnType extends unknown,
>(path: T, data: unknown, defaultValue?: ReturnType): ReturnType | undefined {
  if (isObject(data) && path in data) {
    return (data as Record<T, unknown>)[path] as ReturnType;
  }

  return defaultValue;
}

/**
 * Represents a path through nested objects/arrays.
 *
 * @example
 * type Foo = {
 *   bar: { baz: number }[];
 *   counter: number;
 * };
 * type FooPath = ObjPath<Foo>;
 *
 * // The type looks like this:
 * const foo = {
 *   bar: [{ baz: 1 }, { baz: 2 }],
 *   counter: 0,
 * } satisfies Foo;
 *
 * ["bar", 0, "baz"] satisfies FooPath; // Valid
 * ["bar", 0] satisfies FooPath; // Valid
 * ["bar", "length"] satisfies FooPath; // Valid
 * ["bar"] satisfies FooPath; // Valid
 * ["counter"] satisfies FooPath; // Valid
 *
 * ["bar", 0, "unknown-key"] satisfies FooPath; // Invalid
 * ["unknown-key"] satisfies FooPath; // Invalid
 * ["bar", "unknown-key"] satisfies FooPath; // Invalid
 */
export type ObjPath<T> =
  IsAnyOrUnknown<T> extends true
    ? unknown[]
    : T extends object
      ? T extends (infer U)[]
        ? [keyof []] | [keyof [], ...ObjPath<U>] // Not quite sure why the special handling of arrays is necessary but otherwise this produces some anys
        : [keyof T] | [keyof T, ...ObjPath<T[keyof T]>]
      : [];

/**
 * Follows an `ObjPath` through object `T` to get its type.
 *
 * @example
 * type Foo = {
 *   bar: { baz: number }[];
 *   counter: number;
 * };

 * // The type looks like this:
 * const foo = {
 *   bar: [{ baz: 1 }, { baz: 2 }],
 *   counter: 0,
 * } satisfies Foo;

 * // GetNested<Foo, ["bar", 0, "baz"]> is equivalent to Foo["bar"][0]["baz"]
 * 1 satisfies GetNested<Foo, ["bar", 0, "baz"]>; // Yes, because `foo.bar[0].baz = numVariable` is valid TypeScript
 * ({ baz: 1 }) satisfies GetNested<Foo, ["bar", 0]>; // Valid
 * 1 satisfies GetNested<Foo, ["bar", "length"]>; // Valid
 * [] satisfies GetNested<Foo, ["bar"]>; // Valid
 * [{ baz: 3 }, { baz: 4 }] satisfies GetNested<Foo, ["bar"]>; // Valid
 * 42 satisfies GetNested<Foo, ["counter"]>; // Valid
 *
 * "42" satisfies GetNested<Foo, ["counter"]>; // Invalid
 * 1 satisfies GetNested<Foo, ["bar", 0]>; // Invalid
 */
export type GetNested<T, Path extends ObjPath<T>> =
  IsAnyOrUnknown<T> extends true
    ? unknown
    : Path extends [infer First, ...infer Rest]
      ? First extends keyof T
        ? Rest extends ObjPath<T[First]>
          ? GetNested<T[First], Rest>
          : T[First]
        : never
      : T;

/**
 * Sets a nested value in an object/array (or a mix of both).
 *
 * @param obj The object or array to change
 * @param path The path to the value to change. e.g ["foo", 0, "bar"] will change `obj.foo[0].bar`
 * @param value The value to set
 * @param mutate If `obj` should be mutated. If `false`, the object will be recursively shallow cloned as necessary (e.g if `path` is `["foo", "bar"]`, `obj.baz` won't be cloned)
 * @returns The modified object/array
 *
 * @example
 * // mutate is true
 * const obj = { foo: [{ bar: 1 }, { bar: 2 }] };
 * const sameObj = setNested(obj, ["foo", 0, "bar"], 3, false);
 *
 * console.assert(sameObj === obj) // True
 * console.assert(sameObj.foo[0].bar === 3) // True
 * console.assert(obj.foo[0].bar === 1) // False
 *
 * // mutate is false
 * const obj = { foo: [{ bar: 1 }, { bar: 2 }] };
 * const newObj = setNested(obj, ["foo", 0, "bar"], 3, false);
 *
 * console.assert(obj.foo[0].bar === 1) // True
 * console.assert(newObj.foo[0].bar === 3) // True
 * console.assert(newObj === obj) // False
 */
export function setNested<T extends object>(
  obj: T,
  path: ObjPath<T>,
  value: GetNested<T, typeof path>,
  mutate: boolean
) {
  const newValue = mutate ? obj : shallowClone(obj);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let current = newValue as any;
  for (let i = 0; i < path.length - 1; i++) {
    const key = path[i] as keyof T;

    let next = current[key];
    if (!mutate) {
      next = shallowClone(next);
      current[key] = next; // Otherwise only the final `current` will be updated and not `newValue`
    }
    current = next;
  }
  // `current` is now the parent of where this value is going
  current[path.at(-1) as keyof T] = value;

  return newValue as T;
}

export function getNested<T extends object>(
  obj: T,
  path: ObjPath<T>
): GetNested<T, typeof path> | undefined;

export function getNested<T extends object, DefaultValue>(
  obj: T,
  path: ObjPath<T>,
  defaultValue: DefaultValue
): GetNested<T, typeof path> | DefaultValue;
/**
 * Gets a nested value in an object/array (or a mix of both).
 *
 * @param obj The object or array to get a property from
 * @param path The path to the value to get. e.g ["foo", 0, "bar"] will get `obj.foo[0].bar`
 * @param defaultValue The value to return if the path doesn't exist. Defaults to `undefined`
 * @returns The value or `defaultValue`
 */
export function getNested<T extends object, DefaultValue>(
  obj: T,
  path: ObjPath<T>,
  defaultValue?: DefaultValue
): GetNested<T, typeof path> | DefaultValue | undefined {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let current = obj as any;
  for (const key of path) {
    current = current[key as keyof T];
    if (current == null) {
      return defaultValue;
    }
  }
  return current ?? defaultValue;
}

export function shallowClone<T extends object>(obj: T) {
  if (Array.isArray(obj)) {
    return [...obj] as T;
  }
  return { ...obj } as T;
}

/**
 * A mapped type that transforms object keys by removing a prefix path.
 * For example, given an object with keys like "data.numbers.0.value",
 * and a prefix "data.numbers.0", it will create a new type with keys like "value".
 *
 * @template T - The input object type with dot-notation keys
 * @template P - The prefix path to remove from the keys
 */
type FilteredPaths<T extends Record<string, unknown>, P extends string> = {
  [K in keyof T as K extends `${P}.${infer Rest}` ? Rest : never]: T[K];
};

/**
 * Filters an object by removing a prefix path from all keys and their corresponding values.
 * Only includes entries whose keys start with the given base path.
 *
 * @template T - The input object type with dot-notation keys
 * @template P - The prefix path to remove from the keys
 * @param obj - The input object with dot-notation keys
 * @param basePath - The prefix path to remove from the keys
 * @returns A new object with the prefix removed from all keys
 *
 * @example
 * ```typescript
 * const input = {
 *   "data.numbers.0.value": 42,
 *   "data.numbers.0.valid": true,
 *   "data.numbers.0.tags": ["test"],
 *   "other.field": "value"
 * };
 *
 * const result = filterNestedPaths(input, "data.numbers.0");
 * // Returns:
 * // {
 * //   "value": 42,
 * //   "valid": true,
 * //   "tags": ["test"]
 * // }
 * ```
 */
export function filterNestedPaths<
  T extends Record<string, unknown>,
  P extends string,
>(obj: T, basePath: P): FilteredPaths<T, P> {
  const prefix = `${basePath}.`;
  const result = {} as FilteredPaths<T, P>;

  for (const [path, value] of Object.entries(obj)) {
    if (path.startsWith(prefix)) {
      const newPath = path.slice(prefix.length);
      (result as Record<string, T[keyof T]>)[newPath] = value as T[keyof T];
    }
  }

  return result;
}
